import { CustomTypes, Editor, Operation, Path, Point, Range, Text, Transforms } from 'slate'
import { debounce } from 'lodash-es'

import { TranscriptParagraph } from 'src/models/Transcript'
import { TranscriptClient } from 'src/models/TranscriptClient'
import { ParagraphMergeDirection } from 'src/models/TranscriptOperation'
import { isRtBrokerTranscriptClient } from 'src/network/RtBrokerTranscriptClientV2'
import { rollbarService } from 'src/services/RollbarService'
import type { SlateTextOp } from 'src/models/crdtV2'

import { LINE_BREAK_CHARS, sanitizePastedText } from './sanitizePastedText'
import { TranscriptEditor } from './TranscriptEditor'

// A paste is sent as one update, and Ably rejects a message over 64 KiB.
// This leaves room for the update's own overhead and the clientId.
export const MAX_PASTE_BYTES = 60 * 1024

// Some Android keyboards and IMEs send Enter as text made only of line breaks.
const ONLY_LINE_BREAKS = new RegExp(`^${LINE_BREAK_CHARS.source}+$`)

type WithTranscriptConfig = {
    paragraph: TranscriptParagraph
    transcriptClient: TranscriptClient
    onContentChange?: () => void
    onTextEdit?: () => void
    onPasteTooLarge?: () => void
    /** Read at key time. Absent or false leaves Enter and the edge deletes as no-ops. */
    isParagraphSplitMergeEnabled?: () => boolean
}

type WithTranscriptPlugin = <T extends CustomTypes['Editor']>(config: WithTranscriptConfig) => (editor: T) => T

export const withTranscript: WithTranscriptPlugin = (config) => {
    const { paragraph, transcriptClient } = config

    const onContentChange = debounce(() => config.onContentChange?.(), 500)

    return (editor) => {
        const { apply, onChange, deleteBackward, deleteForward, insertText } = editor
        const textOps: SlateTextOp[] = []

        const queueInsert = (point: Point, text: string) => {
            const at = TranscriptEditor.editableOffsetFromPoint(editor, point)
            if (at === null) {
                return
            }

            textOps.push({ type: 'insert_text', at, text })
        }

        const queueRemove = (point: Point, text: string) => {
            const at = TranscriptEditor.editableOffsetFromPoint(editor, point)
            if (at === null) {
                return
            }

            textOps.push({ type: 'remove_text', at, length: text.length })
        }

        const insertNodePoint = (path: Path): Point =>
            path[path.length - 1] === 0 ? { path, offset: 0 } : Editor.end(editor, Path.previous(path))

        const textAt = (path: Path): Text | null => {
            if (!Editor.hasPath(editor, path)) {
                return null
            }

            const [node] = Editor.node(editor, path)

            return Text.isText(node) ? node : null
        }

        // A paste inserted at `path` is measured from the end of the previous sibling, because the new
        // node is not in the tree yet. That point sits inside the previous leaf, so a paste at the start
        // of an editable run that follows a grey run would look non-editable. The leaf already at `path`
        // is the run the caret is in.
        const pasteInsertPoint = (path: Path): Point | null => {
            const atPrevious = insertNodePoint(path)
            if (TranscriptEditor.editableOffsetFromPoint(editor, atPrevious) !== null) {
                return atPrevious
            }

            const node = textAt(path)
            if (node?.isEditable === true) {
                return { path, offset: 0 }
            }

            return null
        }

        const targetsNonEditableLeaf = (op: Operation): boolean => {
            if (op.type === 'insert_text' || op.type === 'remove_text') {
                const node = textAt(op.path)

                return node !== null && node.isEditable !== true
            }
            if (op.type === 'insert_node' && Text.isText(op.node)) {
                return pasteInsertPoint(op.path) === null
            }

            return false
        }

        editor.apply = (op) => {
            // if a change is coming from yjs, we don't want to apply it back to the transcript.
            // Therefore we create this enable-mechanism to allow us to control what is applied to the transcript.
            // This is enabled by default and has to be disabled explicitly.
            // Remote ops still apply, including ASR updates to the grey tail.
            if (TranscriptEditor.isSyncEditorToTranscriptEnabled(editor)) {
                // A local op on a non-editable leaf is not applied, so the editor stays aligned with the doc.
                if (targetsNonEditableLeaf(op)) {
                    return
                }
                // A paragraph is one element. Enter splits through the doc, never through Slate. Nothing known
                // produces a local split any more, so report one: a caller acting on the new path would be off.
                if (op.type === 'split_node') {
                    rollbarService.warning('[Transcript editor] dropped local split_node', {
                        paragraphId: paragraph.id,
                        path: op.path,
                        stack: new Error().stack,
                    })

                    return
                }

                switch (op.type) {
                    case 'insert_text': {
                        const { text, path, offset } = op
                        queueInsert({ path, offset }, text)
                        break
                    }
                    case 'remove_text': {
                        const { text, path, offset } = op
                        queueRemove({ path, offset }, text)
                        break
                    }
                    // needed for copy/paste
                    case 'insert_node': {
                        const { node, path } = op
                        if (Text.isText(node)) {
                            const at = pasteInsertPoint(path)
                            if (at !== null) {
                                queueInsert(at, node.text)
                            }
                        }
                        break
                    }
                }
            }

            apply(op)

            if (op.type !== 'set_selection' || editor.selection === null) {
                return
            }

            const clamped = TranscriptEditor.clampSelectionToEditable(editor, editor.selection)
            if (clamped === null) {
                Transforms.deselect(editor)

                return
            }
            if (!Range.equals(editor.selection, clamped)) {
                Transforms.select(editor, clamped)
            }
        }

        editor.onChange = () => {
            onChange()

            if (textOps.length === 0) {
                return
            }

            // Before the write, so a failed write still pauses playback.
            config.onTextEdit?.()
            for (const textOp of textOps.splice(0)) {
                transcriptClient.applyParagraphText(paragraph.id, textOp)
            }
            onContentChange()
        }

        // A paragraph is one line: pasted text, and any inserted text with a line break, goes in as one
        // sanitized line, under the same empty and size rules.
        const insertPlainText = (text: string, options?: Parameters<typeof insertText>[1]) => {
            const sanitized = sanitizePastedText(text)
            if (sanitized === '') {
                return
            }
            if (new TextEncoder().encode(sanitized).byteLength > MAX_PASTE_BYTES) {
                config.onPasteTooLarge?.()

                return
            }

            insertText(sanitized, options)
        }

        // Desktop slate-react passes beforeinput string data here without splitting it on line breaks.
        // Typed text never contains one, so it keeps Slate's path.
        editor.insertText = (text, options) => {
            if (ONLY_LINE_BREAKS.test(text)) {
                editor.insertBreak()

                return
            }
            if (LINE_BREAK_CHARS.test(text)) {
                insertPlainText(text, options)

                return
            }

            insertText(text, options)
        }

        // Paste and drop both come through here. Slate's own insertData would split lines into extra
        // elements (and paste a fragment as nodes), so every paste is one plain line.
        editor.insertData = (data) => insertPlainText(data.getData('text/plain'))

        const splitMergeEnabled = () => config.isParagraphSplitMergeEnabled?.() === true && isRtBrokerTranscriptClient(transcriptClient)

        editor.insertBreak = () => {
            if (editor.selection === null || Range.isExpanded(editor.selection) || !splitMergeEnabled()) {
                return
            }

            const atIndex = TranscriptEditor.editableOffsetFromPoint(editor, editor.selection.anchor)
            if (atIndex === null) {
                return
            }

            transcriptClient.splitParagraph(
                { paragraphId: paragraph.id, atIndex, newParagraphId: crypto.randomUUID() },
                { origin: 'transcript_editor' },
            )
        }

        // Shift+Enter would otherwise split the Slate element without splitting the paragraph.
        editor.insertSoftBreak = () => editor.insertBreak()

        const isCollapsedAt = (edge: 'start' | 'end') => {
            const point = TranscriptEditor.editableEdgePoint(editor, edge)

            return (
                editor.selection !== null &&
                Range.isCollapsed(editor.selection) &&
                point !== null &&
                Point.equals(editor.selection.anchor, point)
            )
        }

        const mergeAtEdge = (direction: ParagraphMergeDirection) => {
            if (!splitMergeEnabled()) {
                return
            }

            transcriptClient.mergeParagraph({ paragraphId: paragraph.id, direction }, { origin: 'transcript_editor' })
        }

        editor.deleteBackward = (...args) => {
            if (editor.selection === null) {
                return
            }
            if (isCollapsedAt('start')) {
                mergeAtEdge(ParagraphMergeDirection.Previous)

                return
            }

            deleteBackward(...args)
        }

        editor.deleteForward = (...args) => {
            if (editor.selection === null) {
                return
            }
            if (isCollapsedAt('end')) {
                mergeAtEdge(ParagraphMergeDirection.Next)

                return
            }

            deleteForward(...args)
        }

        return editor
    }
}
